import React, { memo, useState, useMemo } from 'react';
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useUnreadConversationsCount } from '@/hooks/residents/useUnreadConversationsCount';
import {
  MyListing,
  UserMarketplaceStats,
  getConditionDisplayText,
} from '@/types/residents/marketplaceListing';
import { profileStrings } from '@/constants/residents/marketplaceStrings';
import { GlassCard } from '../GlassCard';
import { SectionHeader } from '../SectionHeader';

const ChevronRightIcon = RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT;
const StarFilledIcon = RESIDENTS_SVG_COMPONENTS.MARKETPLACE_STAR;
const StarGrayIcon = RESIDENTS_SVG_COMPONENTS.STAR_GRAY;
const EmptySoldIcon = RESIDENTS_SVG_COMPONENTS.EMPTY_SOLD;
const PropertyPlaceholder = RESIDENTS_SVG_COMPONENTS.PROPERTY_PLACEHOLDER;

export type ProfileNavOption = 'my_profile' | 'saved' | 'inbox';
type ListingTab = 'active' | 'sold';

interface NavTab {
  id: ProfileNavOption;
  title: string;
  subtitle: string;
  accentColor: string;
}

const NAV_TABS: NavTab[] = [
  { id: 'my_profile', title: 'My profile', subtitle: 'Statistics and listings', accentColor: PRIMITIVE_COLORS.resioBlue },
  { id: 'saved', title: 'Saved', subtitle: 'Items for later', accentColor: PRIMITIVE_COLORS.resioPink },
  { id: 'inbox', title: 'Inbox', subtitle: 'Conversations', accentColor: PRIMITIVE_COLORS.resioPurple },
];

interface ProfileContentProps {
  stats: UserMarketplaceStats;
  listings: MyListing[];
  isLoadingListings: boolean;
  onListingPress: (listing: MyListing) => void;
  onFollowingPress: () => void;
  onRatingPress: () => void;
}

// =============================================================================
// ProfileContent — Main export
// =============================================================================

export const ProfileContent: React.FC<ProfileContentProps> = memo(({
  stats,
  listings,
  isLoadingListings,
  onListingPress,
  onFollowingPress,
  onRatingPress,
}) => {
  const [activeTab, setActiveTab] = useState<ListingTab>('active');

  const filteredListings = useMemo(
    () => listings.filter((l) => l.status === activeTab),
    [listings, activeTab],
  );

  return (
    <View style={styles.container}>
      <ProfileNavTabs activeOption="my_profile" />

      <SectionHeader title="Statistics" />
      <ProfileStats
        stats={stats}
        onFollowingPress={onFollowingPress}
        onRatingPress={onRatingPress}
      />

      <SectionHeader title="Listings" />
      <GlassToggleBar
        options={[
          { key: 'active', label: 'Active' },
          { key: 'sold', label: 'Sold' },
        ]}
        activeKey={activeTab}
        onSelect={(key) => setActiveTab(key as ListingTab)}
      />

      <View style={styles.listingsContainer}>
        {isLoadingListings ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={PRIMITIVE_COLORS.resioBlue} />
          </View>
        ) : filteredListings.length > 0 ? (
          filteredListings.map((listing) => (
            <GlassListingRow
              key={listing.id}
              listing={listing}
              onPress={() => onListingPress(listing)}
            />
          ))
        ) : (
          <GlassEmptyState type={activeTab} />
        )}
      </View>
    </View>
  );
});

ProfileContent.displayName = 'ProfileContent';

// =============================================================================
// ProfileNavTabs — exported for reuse in Saved/Inbox screens
// =============================================================================

export const ProfileNavTabs: React.FC<{ activeOption: ProfileNavOption }> = memo(({ activeOption }) => {
  const { colors } = useThemeColors();
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const { unreadCount } = useUnreadConversationsCount();

  const handlePress = (option: ProfileNavOption) => {
    if (option === activeOption) return;
    if (option === 'my_profile') navigation.navigate('MarketplaceProfile');
    else if (option === 'saved') navigation.navigate('SavedListings');
    else if (option === 'inbox') navigation.navigate('MarketplaceInbox');
  };

  return (
    <View style={styles.navTabsRow}>
      {NAV_TABS.map((tab) => {
        const isActive = tab.id === activeOption;
        return (
          <GlassCard
            key={tab.id}
            interactive
            onPress={() => handlePress(tab.id)}
            borderRadius={14}
            padding={14}
            style={[
              styles.navTab,
              isActive && { borderLeftWidth: 3, borderLeftColor: tab.accentColor },
            ]}
          >
            <View style={styles.navTabTextRow}>
              <Text style={[styles.navTabTitle, { color: isActive ? colors.text.primary : colors.text.muted }]}>
                {tab.title}
              </Text>
              {tab.id === 'inbox' && unreadCount > 0 && (
                <View style={styles.unreadDot} />
              )}
            </View>
            <Text style={[styles.navTabSubtitle, { color: isActive ? colors.text.muted : colors.text.dimmer }]}>
              {tab.subtitle}
            </Text>
          </GlassCard>
        );
      })}
    </View>
  );
});

ProfileNavTabs.displayName = 'ProfileNavTabs';

// =============================================================================
// ProfileStats
// =============================================================================

const ProfileStats: React.FC<{
  stats: UserMarketplaceStats;
  onFollowingPress: () => void;
  onRatingPress: () => void;
}> = memo(({ stats, onFollowingPress, onRatingPress }) => {
  const { colors } = useThemeColors();
  return (
    <View style={styles.statsContainer}>
      <GlassCard borderRadius={16} padding={16}>
        <View style={styles.statsTopRow}>
          <StatValue value={stats.bought} label="Bought" />
          <StatValue value={stats.sold} label="Sold" />
          <StatValue value={stats.followers} label="Followers" />
        </View>
      </GlassCard>

      <View style={styles.statsBottomRow}>
        <GlassCard
          interactive
          onPress={onFollowingPress}
          borderRadius={16}
          padding={16}
          style={styles.statsHalfCard}
        >
          <Text style={[styles.statsLabel, { color: colors.text.muted }]}>Following</Text>
          <View style={styles.statsValueRow}>
            <Text style={[styles.statsNumber, { color: colors.text.primary }]}>{stats.following}</Text>
            <ChevronRightIcon width={18} height={18} color={colors.text.dimmer} />
          </View>
        </GlassCard>

        <GlassCard
          interactive
          onPress={onRatingPress}
          borderRadius={16}
          padding={16}
          style={styles.statsHalfCard}
        >
          <StarRatingRow rating={stats.rating} />
          <View style={styles.statsValueRow}>
            <Text style={[styles.statsNumber, { color: colors.text.primary }]}>{stats.reviewCount}</Text>
            <ChevronRightIcon width={18} height={18} color={colors.text.dimmer} />
          </View>
        </GlassCard>
      </View>
    </View>
  );
});

ProfileStats.displayName = 'ProfileStats';

// =============================================================================
// Sub-components
// =============================================================================

const StatValue: React.FC<{ value: number; label: string }> = memo(({ value, label }) => {
  const { colors } = useThemeColors();
  return (
    <View style={styles.statItem}>
      <Text style={[styles.statItemValue, { color: colors.text.primary }]}>{value}</Text>
      <Text style={[styles.statItemLabel, { color: colors.text.muted }]}>{label}</Text>
    </View>
  );
});
StatValue.displayName = 'StatValue';

const StarRatingRow: React.FC<{ rating: number }> = memo(({ rating }) => {
  const filled = Math.floor(rating);
  const stars = [];
  for (let i = 0; i < 5; i++) {
    const Icon = i < filled ? StarFilledIcon : StarGrayIcon;
    stars.push(<Icon key={i} width={14} height={14} />);
  }
  return <View style={styles.starRow}>{stars}</View>;
});
StarRatingRow.displayName = 'StarRatingRow';

// =============================================================================
// GlassToggleBar
// =============================================================================

interface ToggleOption {
  key: string;
  label: string;
}

const GlassToggleBar: React.FC<{
  options: ToggleOption[];
  activeKey: string;
  onSelect: (key: string) => void;
}> = memo(({ options, activeKey, onSelect }) => {
  const { colors } = useThemeColors();
  return (
    <View style={[styles.toggleContainer, { backgroundColor: colors.glass.buttonBg }]}>
      {options.map((option) => {
        const isActive = option.key === activeKey;
        return (
          <TouchableOpacity
            key={option.key}
            style={[styles.toggleOption, isActive && { backgroundColor: colors.accent.blueBg }]}
            onPress={() => onSelect(option.key)}
            activeOpacity={0.7}
          >
            <Text style={[styles.toggleText, { color: isActive ? colors.text.primary : colors.text.muted }]}>
              {option.label}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
});

GlassToggleBar.displayName = 'GlassToggleBar';

// =============================================================================
// GlassListingRow
// =============================================================================

const GlassListingRow: React.FC<{
  listing: MyListing;
  onPress: () => void;
}> = memo(({ listing, onPress }) => {
  const { colors } = useThemeColors();
  const imageUri = listing.images?.[0]?.url;
  const conditionText = getConditionDisplayText(listing.condition);
  const isSold = listing.status === 'sold';

  return (
    <GlassCard
      interactive
      onPress={onPress}
      borderRadius={16}
      padding={16}
      style={styles.listingRow}
    >
      <View style={[styles.listingImageContainer, { borderColor: colors.card.border }]}>
        {imageUri ? (
          <Image source={{ uri: imageUri }} style={styles.listingImage} resizeMode="cover" />
        ) : (
          <View style={[styles.listingPlaceholder, { backgroundColor: colors.card.bg }]}>
            <PropertyPlaceholder width={50} height={50} />
          </View>
        )}
      </View>

      <View style={styles.listingContent}>
        <View style={styles.listingPriceRow}>
          <View style={styles.listingPriceContainer}>
            <Text style={[styles.listingPrice, { color: colors.text.primary }]}>{listing.price}</Text>
            {isSold && (
              <>
                <Text style={[styles.listingPriceDot, { color: colors.text.primary }]}> · </Text>
                <Text style={[styles.listingSoldText, { color: colors.accent.red }]}>Sold</Text>
              </>
            )}
          </View>
          <ChevronRightIcon width={18} height={18} color={colors.text.dimmer} />
        </View>
        <Text style={[styles.listingTitle, { color: colors.text.secondary }]} numberOfLines={2}>{listing.title}</Text>
        <View style={[styles.listingConditionBadge, { backgroundColor: colors.accent.greenLight }]}>
          <Text style={[styles.listingConditionText, { color: colors.accent.green }]}>{conditionText}</Text>
        </View>
      </View>
    </GlassCard>
  );
});

GlassListingRow.displayName = 'GlassListingRow';

// =============================================================================
// GlassEmptyState
// =============================================================================

const GlassEmptyState: React.FC<{ type: 'active' | 'sold' }> = memo(({ type }) => {
  const { colors } = useThemeColors();
  const title = type === 'sold' ? profileStrings.noItemsSold : profileStrings.noActiveListings;
  const subtitle = type === 'sold'
    ? profileStrings.onceYouSell
    : profileStrings.postItemToStart;

  return (
    <View style={styles.emptyContainer}>
      <EmptySoldIcon width={60} height={60} color={colors.text.muted} />
      <Text style={[styles.emptyTitle, { color: colors.text.secondary }]}>{title}</Text>
      {subtitle && <Text style={[styles.emptySubtitle, { color: colors.text.dim }]}>{subtitle}</Text>}
    </View>
  );
});

GlassEmptyState.displayName = 'GlassEmptyState';

// =============================================================================
// Styles
// =============================================================================

const styles = StyleSheet.create({
  container: {
    gap: 4,
  },

  // Nav Tabs
  navTabsRow: {
    flexDirection: 'row',
    gap: 10,
  },
  navTab: {
    flex: 1,
    gap: 2,
  },
  navTabTextRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  navTabTitle: {
    ...REDESIGN_TYPOGRAPHY.buttonSmall,
  },
  navTabSubtitle: {
    ...REDESIGN_TYPOGRAPHY.timestamp,
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: PRIMITIVE_COLORS.warningOrange,
  },

  // Stats
  statsContainer: {
    gap: 10,
  },
  statsTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  statItem: {
    alignItems: 'center',
    minWidth: 52,
  },
  statItemValue: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
  },
  statItemLabel: {
    ...REDESIGN_TYPOGRAPHY.body,
  },
  statsBottomRow: {
    flexDirection: 'row',
    gap: 10,
  },
  statsHalfCard: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statsLabel: {
    ...REDESIGN_TYPOGRAPHY.body,
  },
  statsValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  statsNumber: {
    ...REDESIGN_TYPOGRAPHY.cardTitle,
  },
  starRow: {
    flexDirection: 'row',
    gap: 2,
  },

  // Toggle Bar
  toggleContainer: {
    flexDirection: 'row',
    borderRadius: 10,
    padding: 3,
  },
  toggleOption: {
    flex: 1,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleText: {
    ...REDESIGN_TYPOGRAPHY.tagFilter,
  },

  // Listings
  listingsContainer: {
    gap: 10,
  },
  listingRow: {
    flexDirection: 'row',
    gap: 14,
  },
  listingImageContainer: {
    width: 85,
    height: 85,
    borderRadius: 12,
    borderWidth: 1,
    overflow: 'hidden',
  },
  listingImage: {
    width: '100%',
    height: '100%',
  },
  listingPlaceholder: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  listingContent: {
    flex: 1,
    gap: 3,
  },
  listingPriceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  listingPriceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  listingPrice: {
    ...REDESIGN_TYPOGRAPHY.cardTitle,
  },
  listingPriceDot: {
    ...REDESIGN_TYPOGRAPHY.cardTitle,
  },
  listingSoldText: {
    ...REDESIGN_TYPOGRAPHY.cardTitle,
  },
  listingTitle: {
    ...REDESIGN_TYPOGRAPHY.body,
  },
  listingConditionBadge: {
    alignSelf: 'flex-start',
    borderRadius: 4,
    paddingHorizontal: 7,
    paddingVertical: 2,
  },
  listingConditionText: {
    ...REDESIGN_TYPOGRAPHY.conditionBadge,
  },

  // Empty state
  emptyContainer: {
    alignItems: 'center',
    gap: 12,
    paddingVertical: 40,
  },
  emptyTitle: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
    textAlign: 'center',
  },
  emptySubtitle: {
    ...REDESIGN_TYPOGRAPHY.body,
    textAlign: 'center',
    maxWidth: 280,
  },

  // Loading
  loadingContainer: {
    paddingVertical: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
